import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatCellValue, showLimitError } from '../lib/utils';
import api from '../lib/api';
import { SeatsUsageIndicator } from '../components/SeatsUsageIndicator';
import { BulkImportDialog } from '../components/BulkImportDialog';
import { PageBanner } from '../components/PageBanner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useConfirm } from '../components/ConfirmDialog';
import { Badge } from '../components/ui/badge';
import { KpiCard } from '../components/KpiCard';
import { PageShell } from '../components/PageShell';
import { PageSkeleton } from '../components/PageSkeleton';
import { PageHeader } from '../components/PageHeader';
import { UserAvatar } from '../components/UserAvatar';
import { EmptyState } from '../components/EmptyState';
import { GroupBadges } from '../components/GroupBadges';
import { DataTablePagination } from '../components/DataTableControls';
import { AVATAR_OPTIONS, toAvatarValue } from '../lib/avatarCatalog';
import {
  Users,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Award,
  Flame,
  Zap,
  TrendingUp,
  Eye,
  EyeOff,
  Mail,
  Building2,
  Plus,
  X,
  UserPlus,
  UserCog,
  Lock,
  Send,
  UserX,
  UserCheck,
  MoreVertical,
  Ban,
  RefreshCw,
  CheckCircle,
  Activity,
  Shield,
  XCircle,
  Upload,
  Check,
  ShieldCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ROLE_BADGE_CLASS = 'bg-primary/10 text-primary';
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const ACTION_MENU_WIDTH = 192;
const ACTION_MENU_GUTTER = 8;

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: ROLE_BADGE_CLASS, icon: Shield },
  approver: { label: 'Approver', color: ROLE_BADGE_CLASS, icon: CheckCircle },
  analyst: { label: 'Analyst', color: ROLE_BADGE_CLASS, icon: Activity },
  viewer: { label: 'Viewer', color: ROLE_BADGE_CLASS, icon: Eye },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const Icon = cfg.icon;

  return (
    <Badge className={`inline-flex items-center gap-1 ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}

function CheckboxMark({ checked }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all ${
        checked
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'border-gray-300 bg-white text-transparent dark:border-gray-600 dark:bg-gray-800'
      }`}
    >
      <Check className="h-3.5 w-3.5" />
    </span>
  );
}

function WizardCheckboxRow({ checked, onChange, icon: Icon, title, description, children }) {
  return (
    <label
      className={`group flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-all ${
        checked
          ? 'border-primary/45 bg-primary/5 shadow-sm dark:bg-primary/10'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600 dark:hover:bg-gray-800'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <CheckboxMark checked={checked} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          {Icon && <Icon className={`h-4 w-4 ${checked ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`} />}
          {title}
        </span>
        {description && (
          <span className="mt-1 block text-sm leading-5 text-gray-500 dark:text-gray-400">
            {description}
          </span>
        )}
        {children}
      </span>
    </label>
  );
}
const CONTENT_TEAM_ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'approver', label: 'Approver' },
  { value: 'admin', label: 'Admin' },
];

export default function EmployeesListPage() {
  const { token, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('points');
  const [sortOrder, setSortOrder] = useState('desc');
  const [typeFilter, setTypeFilter] = useState('all_shown');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTerminated, setShowTerminated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef(null);
  const [seatsRefreshKey, setSeatsRefreshKey] = useState(0);
  const seatsUsageRef = useRef(null);
  const addDialogRef = useRef(null);
  const [ConfirmEl, confirm] = useConfirm();
  
  // Add Employee Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [addingEmployee, setAddingEmployee] = useState(false);
  const [addEmployeeStep, setAddEmployeeStep] = useState(0);
  const [authMethod, setAuthMethod] = useState('password'); // 'password' or 'invitation'
  const [availableGroups, setAvailableGroups] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    first_name: '',
    last_name: '',
    job_title: '',
    department: '',
    country: '',
    avatar_url: '',
    password: '',
    confirmPassword: '',
    force_password_change: true,
    send_password_email: false,
    groups: [],
    add_to_content_team: false,
    content_team_role: 'viewer',
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const canInviteEmployees = hasRole(['admin']) || user?.is_superadmin;
  const canResendInvitations = hasRole(['admin', 'analyst']) || user?.is_superadmin;
  const canManageEmployeeStatus = hasRole(['admin']) || user?.is_superadmin;
  const canResetEmployeePassword = hasRole(['admin']) || user?.is_superadmin;
  const canViewSeatsUsage = hasRole(['admin']) || user?.is_superadmin;

  const addEmployeeSteps = [
    { title: 'Identity', description: 'Name, email, and avatar' },
    { title: 'Profile', description: 'Job details and groups' },
    { title: 'Access', description: 'Password or invitation' },
    { title: 'Review', description: 'Confirm and create' },
  ];

  // Password validation
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/\d/.test(password)) errors.push('One number');
    if (!/[@$!%*?&#^()_+\-=[\]{}|;:,.<>]/.test(password)) errors.push('One special character');
    return errors;
  };

  const handlePasswordChange = (password) => {
    setNewEmployee({ ...newEmployee, password });
    setPasswordErrors(validatePassword(password));
  };

  const resetAddEmployeeForm = () => {
    setAddEmployeeStep(0);
    setAuthMethod('password');
    setPasswordErrors([]);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormError(null);
    setNewEmployee({
      email: '',
      first_name: '',
      last_name: '',
      job_title: '',
      department: '',
      country: '',
      avatar_url: '',
      password: '',
      confirmPassword: '',
      force_password_change: true,
      send_password_email: false,
      groups: [],
      add_to_content_team: false,
      content_team_role: 'viewer',
    });
  };

  const closeAddEmployeeDialog = () => {
    setShowAddDialog(false);
    resetAddEmployeeForm();
  };

  useEffect(() => {
    if (formError) {
      addDialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formError]);

  useEffect(() => {
    if (!activeActionMenu) return;

    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActiveActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeActionMenu]);

  const fetchEmployees = useCallback(async () => {
    try {
      const url = showTerminated
        ? `${API_URL}/api/employees?limit=200&include_terminated=true`
        : `${API_URL}/api/employees?limit=200`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  }, [token, showTerminated]);

  useEffect(() => {
    fetchEmployees();

    const fetchGroups = async () => {
      if (!canInviteEmployees) {
        setAvailableGroups([]);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          // Filter out "All Employees" since all users are implicitly in that group
          const groups = (Array.isArray(data) ? data : data.groups || [])
            .filter(g => g.slug !== 'all-employees');
          setAvailableGroups(groups);
        }
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      }
    };

    fetchGroups();
  }, [token, showTerminated, fetchEmployees, canInviteEmployees]);

  const handleCreateEmployee = async () => {
    // Validation
    if (!newEmployee.email || !newEmployee.first_name || !newEmployee.last_name) {
      toast.error('Error', { description: 'Email, first name, and last name are required' });
      return;
    }
    if (!newEmployee.country) {
      toast.error('Error', { description: 'Country is required' });
      return;
    }
    
    if (authMethod === 'password') {
      if (!newEmployee.password) {
        toast.error('Error', { description: 'Password is required' });
        return;
      }
      if (passwordErrors.length > 0) {
        toast.error('Error', { description: 'Please fix password requirements' });
        return;
      }
      if (newEmployee.password !== newEmployee.confirmPassword) {
        toast.error('Error', { description: 'Passwords do not match' });
        return;
      }
    }
    
    setAddingEmployee(true);
    try {
      const payload = {
        email: newEmployee.email,
        first_name: newEmployee.first_name,
        last_name: newEmployee.last_name,
        job_title: newEmployee.job_title || null,
        department: newEmployee.department || null,
        country: newEmployee.country || null,
        avatar_url: newEmployee.avatar_url || null,
        groups: newEmployee.groups || [],
        force_password_change: Boolean(newEmployee.force_password_change),
        send_password_email: Boolean(newEmployee.send_password_email),
        add_to_content_team: Boolean(newEmployee.add_to_content_team),
        content_team_role: newEmployee.add_to_content_team ? newEmployee.content_team_role : null,
      };

      if (authMethod === 'password') {
        payload.password = newEmployee.password;
        payload.send_invitation = false;
      } else {
        payload.send_invitation = true;
      }

      await api.post('/employees', payload);
      toast.success('Employee Added', {
        description: authMethod === 'password'
          ? `${newEmployee.first_name} can now log in with their password.`
          : `Invitation sent to ${newEmployee.email}`,
      });
      closeAddEmployeeDialog();
      fetchEmployees();
      setSeatsRefreshKey(prev => prev + 1);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = showLimitError(detail || error?.message, navigate, 'Failed to add employee');
      setFormError(msg);
    } finally {
      setAddingEmployee(false);
    }
  };

  const handleAddEmployeeSubmit = (e) => {
    e.preventDefault();
  };

  const validateAddEmployeeStep = () => {
    if (addEmployeeStep === 0 && (!newEmployee.email || !newEmployee.first_name || !newEmployee.last_name)) {
      toast.error('Error', { description: 'Email, first name, and last name are required' });
      return false;
    }
    if (addEmployeeStep === 1 && !newEmployee.country) {
      toast.error('Error', { description: 'Country is required' });
      return false;
    }
    if (addEmployeeStep === 2 && authMethod === 'password') {
      if (!newEmployee.password) {
        toast.error('Error', { description: 'Password is required' });
        return false;
      }
      if (passwordErrors.length > 0) {
        toast.error('Error', { description: 'Please fix password requirements' });
        return false;
      }
      if (newEmployee.password !== newEmployee.confirmPassword) {
        toast.error('Error', { description: 'Passwords do not match' });
        return false;
      }
    }
    return true;
  };

  const goToNextAddEmployeeStep = () => {
    if (!validateAddEmployeeStep()) return;
    setAddEmployeeStep((step) => Math.min(step + 1, addEmployeeSteps.length - 1));
  };

  const handleResendInvitation = async (e, employee) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/api/employees/${employee.id || employee.user_id}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Invitation Sent', { description: `Invitation resent to ${employee.email}` });
      } else {
        const error = await response.json();
        toast.error('Error', { description: error.detail || 'Failed to resend invitation' });
      }
    } catch (_error) {
      toast.error('Error', { description: 'Failed to resend invitation' });
    }
  };

  const handleResetPassword = async (e, employee) => {
    e.stopPropagation();
    setActiveActionMenu(null);

    const displayName = employee.name || employee.first_name || employee.email;
    const ok = await confirm({
      title: 'Reset Employee Password',
      description: `Send a password reset email to ${displayName}?`,
      confirmLabel: 'Send Reset Email',
    });
    if (!ok) return;

    try {
      const { data } = await api.post(`/employees/${employee.id || employee.user_id}/reset-password`);
      toast.success('Password Reset Sent', { description: data.message || `Password reset email sent to ${employee.email}` });
    } catch (error) {
      toast.error('Error', { description: error?.response?.data?.detail || 'Failed to send password reset email' });
    }
  };

  const handleStatusChange = async (e, employee, newStatus) => {
    e.stopPropagation();
    setActiveActionMenu(null);
    
    // Confirm for destructive actions
    if (newStatus === 'terminated') {
      const ok = await confirm({ title: 'Terminate Employee', description: `Are you sure you want to terminate ${employee.first_name || employee.email}? This action cannot be easily undone.`, confirmLabel: 'Terminate', variant: 'danger' });
      if (!ok) return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/employees/${employee.id || employee.user_id}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Status Updated', { description: result.message });
        // Refresh the list
        fetchEmployees();
        // Refresh seats usage indicator (status changes affect active user count)
        setSeatsRefreshKey(prev => prev + 1);
      } else {
        const error = await response.json();
        toast.error('Error', { description: error.detail || 'Failed to update status' });
      }
    } catch (_error) {
      toast.error('Error', { description: 'Failed to update status' });
    }
  };

  const filteredEmployees = employees
    .filter(e => {
      if (typeFilter === 'employees') return !e.is_content_team;
      if (typeFilter === 'content_team') return e.is_content_team;
      return true;
    })
    .filter(e => {
      if (statusFilter === 'active') return e.status === 'active';
      if (statusFilter === 'pending') return e.status === 'pending';
      if (statusFilter === 'inactive') return e.status === 'inactive';
      if (statusFilter === 'terminated') return e.status === 'terminated';
      return true;
    })
    .filter(e => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        (e.email && e.email.toLowerCase().includes(query)) ||
        (e.name && e.name.toLowerCase().includes(query)) ||
        (e.first_name && e.first_name.toLowerCase().includes(query)) ||
        (e.last_name && e.last_name.toLowerCase().includes(query)) ||
        (e.department && e.department.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField] || 0;
      let bVal = b[sortField] || 0;
      if (typeof aVal === 'string') {
        return sortOrder === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });
  const totalPages = Math.max(Math.ceil(filteredEmployees.length / pageSize), 1);
  const pagedEmployees = filteredEmployees.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasActiveFilters = Boolean(
    searchQuery ||
    typeFilter !== 'all_shown' ||
    statusFilter !== 'all' ||
    showTerminated
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, statusFilter, showTerminated, sortField, sortOrder, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const seatCountedEmployees = useMemo(
    () => employees.filter((e) => !e.is_content_team && ['active', 'pending'].includes(e.status)),
    [employees]
  );
  const contentTeamShown = useMemo(
    () => employees.filter((e) => e.is_content_team && ['active', 'pending'].includes(e.status)),
    [employees]
  );
  const activeEmployees = useMemo(
    () => employees.filter((e) => e.status === 'active'),
    [employees]
  );
  // Content team members are billed as seats too, so they contribute to the seat count. (EIP-204)
  const totalSeatsInUse = seatCountedEmployees.length + contentTeamShown.length;

  const getContentTeamRole = (employee) => {
    const roles = employee.content_team_roles || [];
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('approver')) return 'approver';
    if (roles.includes('analyst')) return 'analyst';
    if (roles.includes('viewer')) return 'viewer';
    return null;
  };

  const SortButton = ({ field, label }) => (
    <button
      onClick={() => {
        if (sortField === field) {
          setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
          setSortField(field);
          setSortOrder('desc');
        }
      }}
      className="flex items-center gap-1 hover:text-primary transition-colors"
    >
      {label}
      {sortField === field && (sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />)}
    </button>
  );

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="people-employees"
        kpis={4}
        sections={2}
        sectionClassName="h-80"
        data-testid="employees-loading"
      />
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="employees-page">
      {ConfirmEl}
      {/* Page Banner */}
      <PageBanner 
        pageKey="people-employees"
      />
      
      <PageHeader
        title="Employees"
        className="mb-6"
        description={`${employees.length} employees • Click to view details`}
        actions={canInviteEmployees && (
          <>
            <button
              onClick={() => setShowBulkImportDialog(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-primary/40 text-primary hover:bg-primary/10 rounded-lg transition-colors"
              data-testid="bulk-import-btn"
            >
              <Upload className="w-4 h-4" />
              Bulk Import
            </button>
            <button
              onClick={() => { resetAddEmployeeForm(); setShowAddDialog(true); }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors"
              data-testid="add-employee-btn"
            >
              <UserPlus className="w-4 h-4" />
              Add Employee
            </button>
          </>
        )}
      />

      {/* Stats Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <KpiCard title="Active" value={activeEmployees.length} subtitle="Currently active" icon={Users} color="primary" />
        <KpiCard title="Seat Counted" value={totalSeatsInUse} icon={TrendingUp} color="success" />
        <button type="button" className="text-left" onClick={() => setStatusFilter('pending')}>
          <KpiCard title="Pending" value={employees.filter(e => e.status === 'pending').length} icon={Mail} color="warning" />
        </button>
        <KpiCard title="Content Team" value={contentTeamShown.length} icon={Zap} color="amber" />
      </div>

      <div className="space-y-6">
          {canViewSeatsUsage && (
            <SeatsUsageIndicator
              type="seats"
              refreshKey={seatsRefreshKey}
              ref={seatsUsageRef}
              caption={`${totalSeatsInUse} seat${totalSeatsInUse === 1 ? '' : 's'} in use = ${seatCountedEmployees.length} employee${seatCountedEmployees.length === 1 ? '' : 's'} + ${contentTeamShown.length} content team member${contentTeamShown.length === 1 ? '' : 's'}.`}
              explanation="Employees and content team share one seat pool."
            />
          )}

      {/* Employees Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="space-y-4 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Employee Directory</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Search, filter, and manage employees in this workspace.
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or department..."
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="type-filter"
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-auto"
              >
                <option value="all_shown">All Shown</option>
                <option value="employees">Employees Only</option>
                <option value="content_team">Content Team Only</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-auto"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="pending">Pending Invitations</option>
                <option value="inactive">Inactive Only</option>
                {showTerminated && <option value="terminated">Terminated</option>}
              </select>
              <label className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 sm:w-auto">
                <input
                  type="checkbox"
                  checked={showTerminated}
                  onChange={(e) => setShowTerminated(e.target.checked)}
                  className="h-4 w-4 rounded text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Show Terminated</span>
              </label>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] data-table">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Groups
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortButton field="level" label="Level" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortButton field="points" label="Points" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortButton field="current_streak" label="Streak" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {pagedEmployees.map((employee) => (
                <tr
                  key={employee.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/people/employees/${employee.id || employee.user_id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <UserAvatar user={employee} size="md" />
                      <div className="ml-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email?.split('@')[0]}
                          </div>
                          {(() => {
                            const role = employee.is_content_team && getContentTeamRole(employee);
                            if (!role) return null;
                            return <RoleBadge role={role} />;
                          })()}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{employee.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Building2 className="w-4 h-4" />
                      {formatCellValue(employee.department)}
                    </div>
                  </td>
                  <td className="px-6 py-4 max-w-[240px]">
                    <GroupBadges groups={employee.groups} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {employee.level || 1}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {employee.points || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Flame className={`w-4 h-4 ${employee.current_streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {employee.current_streak || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      employee.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : employee.status === 'pending'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                        : employee.status === 'inactive'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        : employee.status === 'terminated'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {employee.status === 'pending' ? '⏳ Pending' 
                        : employee.status === 'terminated' ? '🚫 Terminated'
                        : employee.status === 'inactive' ? '⏸️ Inactive'
                        : employee.status || 'active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1 relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/people/employees/${employee.id || employee.user_id}`);
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="View details"
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      {(canManageEmployeeStatus || canResetEmployeePassword || (canResendInvitations && employee.status === 'pending')) && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const eid = employee.id || employee.user_id;
                              if (activeActionMenu === eid) {
                                setActiveActionMenu(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const left = Math.min(
                                  Math.max(ACTION_MENU_GUTTER, rect.right - ACTION_MENU_WIDTH),
                                  window.innerWidth - ACTION_MENU_WIDTH - ACTION_MENU_GUTTER
                                );
                                setMenuPosition({ top: rect.bottom + ACTION_MENU_GUTTER, left });
                                setActiveActionMenu(eid);
                              }
                            }}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            data-testid={`employee-actions-${employee.id || employee.user_id}`}
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          {activeActionMenu === (employee.id || employee.user_id) && createPortal((
                            <div
                              ref={actionMenuRef}
                              className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1"
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                            >
                              {canManageEmployeeStatus && employee.status === 'active' && (
                                <>
                                  {canResetEmployeePassword && (
                                    <button
                                      onClick={(e) => handleResetPassword(e, employee)}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                    >
                                      <Lock className="w-4 h-4 text-gray-500" />
                                      Reset Password
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => handleStatusChange(e, employee, 'inactive')}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    <Ban className="w-4 h-4 text-gray-500" />
                                    Deactivate
                                  </button>
                                  <button
                                    onClick={(e) => handleStatusChange(e, employee, 'terminated')}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                  >
                                    <UserX className="w-4 h-4" />
                                    Terminate
                                  </button>
                                </>
                              )}
                              {canManageEmployeeStatus && employee.status === 'inactive' && (
                                <>
                                  <button
                                    onClick={(e) => handleStatusChange(e, employee, 'active')}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                    Reactivate
                                  </button>
                                  <button
                                    onClick={(e) => handleStatusChange(e, employee, 'terminated')}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                  >
                                    <UserX className="w-4 h-4" />
                                    Terminate
                                  </button>
                                </>
                              )}
                              {canManageEmployeeStatus && employee.status === 'terminated' && (
                                <button
                                  onClick={(e) => handleStatusChange(e, employee, 'active')}
                                  className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Reinstate
                                </button>
                              )}
                              {employee.status === 'pending' && (
                                <>
                                  {canResendInvitations && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveActionMenu(null);
                                        handleResendInvitation(e, employee);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2"
                                    >
                                      <Mail className="w-4 h-4" />
                                      Resend Invitation
                                    </button>
                                  )}
                                  {canManageEmployeeStatus && (
                                    <button
                                      onClick={(e) => handleStatusChange(e, employee, 'terminated')}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                    >
                                      <UserX className="w-4 h-4" />
                                      Cancel Invitation
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          ), document.body)}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length > 0 && (
          <div className="px-4 pb-4 pt-4 sm:px-6">
            <DataTablePagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={filteredEmployees.length}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setCurrentPage}
              onPageSizeChange={(value) => setPageSize(value)}
              itemLabel="employees"
              pageSizeTestId="employees-page-size"
            />
          </div>
        )}
        
        {filteredEmployees.length === 0 && (
          <EmptyState
            icon={Users}
            title={hasActiveFilters ? 'No employees match the current filters' : 'No employees found'}
            description={hasActiveFilters ? 'Clear filters to see the full employee directory.' : 'Invite employees to start building your Eva workspace.'}
            actionLabel={hasActiveFilters ? 'Clear filters' : 'Add employee'}
            actionIcon={hasActiveFilters ? X : UserPlus}
            onAction={
              hasActiveFilters
                ? () => {
                    setSearchQuery('');
                    setTypeFilter('all_shown');
                    setStatusFilter('all');
                    setShowTerminated(false);
                  }
                : canInviteEmployees
                  ? () => { resetAddEmployeeForm(); setShowAddDialog(true); }
                  : null
            }
            variant="plain"
          />
        )}
      </div>
      </div>

      {/* Add Employee Dialog */}
      {showAddDialog && createPortal((
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            ref={addDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-employee-dialog-title"
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-700 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-3">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 id="add-employee-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">Add New Employee</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Create an employee profile and access method</p>
                </div>
              </div>
              <button
                onClick={closeAddEmployeeDialog}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="border-b border-gray-200 bg-gray-50 px-6 py-5 dark:border-gray-700 dark:bg-gray-900/50">
              <div className="mx-auto flex max-w-2xl items-center justify-between">
                {addEmployeeSteps.map((step, index) => {
                  const stepNum = index + 1;
                  const isActive = addEmployeeStep === index;
                  const isComplete = addEmployeeStep > index;
                  return (
                    <div key={step.title} className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          if (index <= addEmployeeStep || validateAddEmployeeStep()) {
                            setAddEmployeeStep(index);
                          }
                        }}
                        className={`flex items-center gap-2 transition-colors ${
                          isActive ? 'text-primary' : isComplete ? 'text-emerald-600' : 'text-gray-400'
                        }`}
                      >
                        <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : isComplete
                              ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                        }`}>
                          {isComplete ? <CheckCircle className="h-4 w-4" /> : stepNum}
                        </span>
                        <span className="hidden text-sm font-medium sm:inline">{step.title}</span>
                      </button>
                      {index < addEmployeeSteps.length - 1 && (
                        <ChevronRight className="mx-2 h-5 w-5 text-gray-300 dark:text-gray-600" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleAddEmployeeSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 overflow-y-auto space-y-6 p-5 sm:p-7">
                {formError && (
                  <Alert variant="destructive">
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                )}

              {addEmployeeStep === 0 && (
                <div className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => { setNewEmployee({ ...newEmployee, email: e.target.value }); if (formError) setFormError(null); }}
                  placeholder="employee@company.com"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              
              {/* Name */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
                    placeholder="John"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
                    placeholder="Doe"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
              </div>
                </div>
              )}
              
              {addEmployeeStep === 1 && (
                <div className="space-y-5">
              {/* Job Title & Department */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={newEmployee.job_title}
                    onChange={(e) => setNewEmployee({ ...newEmployee, job_title: e.target.value })}
                    placeholder="Software Engineer"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={newEmployee.department}
                    onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                    placeholder="Engineering"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              
              {/* Country */}
              <div>
	                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
	                  Country <span className="text-red-500">*</span>
	                </label>
	                <select
	                  value={newEmployee.country}
	                  onChange={(e) => setNewEmployee({ ...newEmployee, country: e.target.value })}
	                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
	                  required
	                >
                  <option value="">Select country...</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Spain">Spain</option>
                  <option value="Italy">Italy</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Austria">Austria</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Norway">Norway</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Finland">Finland</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Poland">Poland</option>
                  <option value="Portugal">Portugal</option>
                  <option value="India">India</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="China">China</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Hong Kong">Hong Kong</option>
                  <option value="Brazil">Brazil</option>
                  <option value="Mexico">Mexico</option>
                  <option value="Argentina">Argentina</option>
                  <option value="South Africa">South Africa</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Israel">Israel</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Avatar */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Avatar
                  </label>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <UserAvatar
                      user={{
                        name: `${newEmployee.first_name || ''} ${newEmployee.last_name || ''}`.trim() || newEmployee.email,
                        email: newEmployee.email,
                        avatar_url: newEmployee.avatar_url,
                      }}
                      size="sm"
                    />
                    <span>{newEmployee.avatar_url ? 'Selected' : 'Auto-assigned'}</span>
                  </div>
                </div>
                <div className="grid grid-cols-8 gap-2 rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-700">
                  {AVATAR_OPTIONS.map((avatar) => {
                    const Icon = avatar.Icon;
                    const avatarValue = toAvatarValue(avatar.id);
                    const selected = newEmployee.avatar_url === avatarValue;
                    return (
                      <button
                        key={avatar.id}
                        type="button"
                        aria-label={`Use ${avatar.label} avatar`}
                        title={avatar.label}
                        onClick={() => setNewEmployee({ ...newEmployee, avatar_url: avatarValue })}
                        className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all hover:scale-105 ${avatar.bgClass} ${avatar.textClass} ${
                          selected ? 'border-primary ring-2 ring-primary/40' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Groups Selection */}
              {availableGroups.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assign to Groups
                  </label>
                  <div className="max-h-44 space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/60">
                    {availableGroups.map((group) => (
                      <label
                        key={group.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors ${
                          newEmployee.groups.includes(group.id)
                            ? 'bg-primary/5 dark:bg-primary/10'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={newEmployee.groups.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewEmployee({
                                ...newEmployee,
                                groups: [...newEmployee.groups, group.id],
                              });
                            } else {
                              setNewEmployee({
                                ...newEmployee,
                                groups: newEmployee.groups.filter((id) => id !== group.id),
                              });
                            }
                          }}
                          className="sr-only"
                        />
                        <CheckboxMark checked={newEmployee.groups.includes(group.id)} />
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-7 w-7 items-center justify-center rounded-lg border bg-white/80 dark:bg-gray-900/70"
                            style={{
                              color: group.color || 'hsl(var(--primary))',
                              borderColor: group.color ? `${group.color}55` : 'hsl(var(--border))',
                            }}
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {group.name}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Select one or more groups to assign this employee to
                  </p>
                </div>
              )}
                </div>
              )}
              
              {addEmployeeStep === 2 && (
                <div className="space-y-5">
              <div className="space-y-4">
                <WizardCheckboxRow
                  checked={newEmployee.add_to_content_team}
                  onChange={(checked) => setNewEmployee({ ...newEmployee, add_to_content_team: checked })}
                  icon={UserCog}
                  title="Add to Content Team"
                  description="Content team members can access Eva Insights. They use the shared seat pool, the same as employees."
                />
                {newEmployee.add_to_content_team && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 dark:bg-primary/10">
                    <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Content team role
                    </label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {CONTENT_TEAM_ROLE_OPTIONS.map((role) => {
                        const roleConfig = ROLE_CONFIG[role.value] || ROLE_CONFIG.viewer;
                        const Icon = roleConfig.icon;
                        const selected = newEmployee.content_team_role === role.value;
                        return (
                          <button
                            key={role.value}
                            type="button"
                            onClick={() => setNewEmployee({ ...newEmployee, content_team_role: role.value })}
                            className={`inline-flex items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-all ${
                              selected
                                ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:bg-primary/5 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-primary/10'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {role.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Authentication Method */}
              <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  How should this employee access the system?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAuthMethod('password')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      authMethod === 'password'
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Lock className={`w-5 h-5 mb-1 ${authMethod === 'password' ? 'text-primary' : 'text-gray-400'}`} />
                    <div className={`font-medium ${authMethod === 'password' ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                      Set Password
                    </div>
                    <div className="text-xs text-gray-500">Create password now</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMethod('invitation')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      authMethod === 'invitation'
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Send className={`w-5 h-5 mb-1 ${authMethod === 'invitation' ? 'text-primary' : 'text-gray-400'}`} />
                    <div className={`font-medium ${authMethod === 'invitation' ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                      Send Invitation
                    </div>
                    <div className="text-xs text-gray-500">Email invite to set password</div>
                  </button>
                </div>
              </div>
              
              {/* Password Fields (if password method selected) */}
              {authMethod === 'password' && (
                <div className="space-y-4 pt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newEmployee.password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        placeholder="Create a strong password"
                        className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password strength indicator */}
                    {newEmployee.password && passwordErrors.length > 0 && (
                      <div className="mt-2 text-xs space-y-1">
                        <p className="text-gray-500 dark:text-gray-400 font-medium">Password must contain:</p>
                        <ul className="space-y-1">
                          {passwordErrors.map((err, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-red-500">
                              <XCircle className="w-3 h-3" />
                              {err}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {newEmployee.password && passwordErrors.length === 0 && (
                      <div className="mt-2 text-xs flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        Password meets all requirements
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={newEmployee.confirmPassword}
                        onChange={(e) => setNewEmployee({ ...newEmployee, confirmPassword: e.target.value })}
                        placeholder="Confirm password"
                        className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {newEmployee.confirmPassword && newEmployee.password !== newEmployee.confirmPassword && (
                      <p className="mt-1 text-xs text-red-500 flex items-center gap-2">
                        <XCircle className="w-3 h-3" />
                        Passwords do not match
                      </p>
                    )}
                    {newEmployee.confirmPassword && newEmployee.password === newEmployee.confirmPassword && newEmployee.password && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-2">
                        <CheckCircle className="w-3 h-3" />
                        Passwords match
                      </p>
                    )}
                  </div>
                  <WizardCheckboxRow
                    checked={newEmployee.force_password_change}
                    onChange={(checked) => setNewEmployee({ ...newEmployee, force_password_change: checked })}
                    icon={Lock}
                    title="Require password change on first login"
                    description="Use this when the password is temporary or shared outside Eva."
                  />
                  <WizardCheckboxRow
                    checked={newEmployee.send_password_email}
                    onChange={(checked) => setNewEmployee({ ...newEmployee, send_password_email: checked })}
                    icon={Mail}
                    title="Send account-ready email"
                    description="The email will not include the password."
                  />
                </div>
              )}
              
              {authMethod === 'invitation' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
                  <Mail className="w-4 h-4 inline mr-2" />
                  An email invitation will be sent to the employee to set their own password.
                </div>
              )}
                </div>
              )}

              {addEmployeeStep === 3 && (
                <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      user={{
                        name: `${newEmployee.first_name || ''} ${newEmployee.last_name || ''}`.trim() || newEmployee.email,
                        email: newEmployee.email,
                        avatar_url: newEmployee.avatar_url,
                      }}
                      size="md"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {`${newEmployee.first_name || ''} ${newEmployee.last_name || ''}`.trim()}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{newEmployee.email}</p>
                    </div>
                  </div>
                  <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Department</dt>
                      <dd className="font-medium text-gray-900 dark:text-white">{newEmployee.department || 'Not set'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Country</dt>
                      <dd className="font-medium text-gray-900 dark:text-white">{newEmployee.country || 'Not set'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Access</dt>
                      <dd className="font-medium text-gray-900 dark:text-white">
                        {authMethod === 'password' ? 'Password set by admin' : 'Invitation email'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Content Team</dt>
                      <dd className="font-medium text-gray-900 dark:text-white">
                        {newEmployee.add_to_content_team
                          ? <RoleBadge role={newEmployee.content_team_role} />
                          : 'No'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">First login</dt>
                      <dd className="font-medium text-gray-900 dark:text-white">
                        {authMethod === 'password' && newEmployee.force_password_change ? 'Password change required' : 'Standard'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
              
              </div>

              {/* Actions */}
              <div className="flex gap-3 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={addEmployeeStep === 0 ? closeAddEmployeeDialog : () => setAddEmployeeStep((step) => Math.max(step - 1, 0))}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {addEmployeeStep === 0 ? 'Cancel' : 'Back'}
                </button>
                {addEmployeeStep < addEmployeeSteps.length - 1 ? (
                  <button
                    type="button"
                    onClick={goToNextAddEmployeeStep}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleCreateEmployee}
                    disabled={addingEmployee}
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addingEmployee ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Add Employee
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        isOpen={showBulkImportDialog}
        onClose={() => setShowBulkImportDialog(false)}
        onSuccess={() => {
          fetchEmployees();
          setSeatsRefreshKey(prev => prev + 1);
        }}
      />
    </PageShell>
  );
}
